import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Offcanvas } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { ProductContext } from '../../ProductProvider';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import Preloader from '../../components/Preloader/Preloader';
import { Helmet } from 'react-helmet-async';
import Barcode from 'react-barcode';
import BookCover from '../../components/BookCover/BookCover';
import './MyBooks.css';

const config = require('../../config.json');

export default function MyBooks() {
  const { validateLogin, loggedUser, myProfileImage, generateDate } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();

  const [issuedItems, setIssuedItems] = useState([]);
  const [loaded, setLoaded] = useState(true);

  // Search & Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('ALL'); // 'ALL' | 'BOOKS' | 'JOURNALS' | 'OVERDUE' | 'DUE_SOON'
  const [sortField, setSortField] = useState('bookedTo');
  const [sortOrder, setSortOrder] = useState('asc');

  // Staff Member Lookup State (for Librarians/Admins)
  const [lookupMemberId, setLookupMemberId] = useState('');
  const [activeMember, setActiveMember] = useState({
    id: '',
    name: '',
    role: '',
    avatar: ''
  });

  // Selected item for detail drawer
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);

  const isStaff = loggedUser.isAdmin ||
    loggedUser.profile === 'Admin' ||
    loggedUser.profile === 'Super Admin' ||
    loggedUser.profile === 'Librarian' ||
    loggedUser.profile === 'Non-Teaching Staff';

  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    } else {
      const initialMemberId = loggedUser.nickname || '';
      setActiveMember({
        id: initialMemberId,
        name: loggedUser.name || 'Member',
        role: loggedUser.profile || 'Student',
        avatar: myProfileImage
      });
      if (initialMemberId) {
        fetchIssuedItems(initialMemberId);
      }
    }
  }, [loggedUser]);

  // Helper to classify item type (Book vs Journal vs Bound Volume)
  const detectItemType = (item) => {
    const title = (item.bookTitle || item.title || '').toLowerCase();
    const acc = (item.accessionNo || '').toUpperCase();
    const callNo = (item.bookClassificationNo || item.classificationNo || '').toUpperCase();
    const cat = (item.bookCategory || item.category || '').toLowerCase();

    if (item.journalID || item.volissuesNo || item.articleID || title.includes('journal') || cat.includes('journal')) {
      return 'Journal';
    }
    if (acc.startsWith('J-') || acc.startsWith('JRN') || acc.startsWith('PER') || callNo.includes('PER') || callNo.includes('050')) {
      return 'Journal';
    }
    if (acc.startsWith('BV-') || title.includes('bound vol') || cat.includes('bound')) {
      return 'Bound Volume';
    }
    return 'Book';
  };

  // Fetch all issued items for a specific member ID
  const fetchIssuedItems = async (memberId) => {
    if (!memberId) return;
    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = "VJCollege";
      const tempTopic = "UserID";

      const response = await axios.get(
        `${config.api.invokeURL}/vjlibrary/issue?fetchKey=${memberId}&fetchOption=${tempTopic}&clientID=${clientID}`,
        {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        }
      );

      const rawData = Array.isArray(response.data) ? response.data : [];
      const todayDateStr = typeof generateDate === 'function' ? generateDate() : new Date().toISOString().slice(0, 10);
      const todayDate = new Date(todayDateStr);

      const processed = rawData.map((item) => {
        const bookedOnDate = item.bookedOn ? new Date(item.bookedOn) : new Date();
        const bookedToDate = item.bookedTo ? new Date(item.bookedTo) : null;

        // Days elapsed
        const diffTime = todayDate - bookedOnDate;
        const daysIssued = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));

        // Days left or overdue
        let daysLeft = 0;
        let overdueDays = 0;
        let status = 'ACTIVE';

        if (bookedToDate) {
          const diffTo = bookedToDate - todayDate;
          daysLeft = Math.ceil(diffTo / (1000 * 60 * 60 * 24));

          if (daysLeft < 0) {
            overdueDays = Math.abs(daysLeft);
            status = 'OVERDUE';
          } else if (daysLeft === 0) {
            status = 'DUE_TODAY';
          } else if (daysLeft <= 3) {
            status = 'DUE_SOON';
          } else {
            status = 'ACTIVE';
          }
        }

        // Nominal Fine calculation (₹1 / day after overdue)
        let fine = 0;
        if (overdueDays > 0) {
          fine = overdueDays * 1;
        }

        const itemType = detectItemType(item);

        return {
          ...item,
          itemType,
          daysIssued,
          daysLeft,
          overdueDays,
          fine,
          status
        };
      });

      setIssuedItems(processed);
      enqueueSnackbar(`${processed.length} Circulation records loaded`, { variant: 'success' });
    } catch (error) {
      console.error("Error loading issued items:", error);
      enqueueSnackbar("Error loading circulation data: " + (error.response?.data?.message || error.message), { variant: 'warning' });
    } finally {
      setLoaded(false);
      setLoaded(true);
    }
  };

  // Staff member lookup handler
  const handleStaffLookup = (e) => {
    e.preventDefault();
    if (!lookupMemberId.trim()) {
      enqueueSnackbar("Please enter a Member Registration ID", { variant: 'warning' });
      return;
    }
    const cleanId = lookupMemberId.trim();
    setActiveMember({
      id: cleanId,
      name: `Member ${cleanId}`,
      role: 'Queried Member',
      avatar: 'https://s3.ap-south-1.amazonaws.com/idntica.com/avatar/profileImage/defaultAvatar.jpeg'
    });
    fetchIssuedItems(cleanId);
  };

  const handleResetToMyLoans = () => {
    setLookupMemberId('');
    setActiveMember({
      id: loggedUser.nickname || '',
      name: loggedUser.name || 'My Loans',
      role: loggedUser.profile || 'Student',
      avatar: myProfileImage
    });
    if (loggedUser.nickname) {
      fetchIssuedItems(loggedUser.nickname);
    }
  };

  // Max book quota according to member tier
  const maxQuota = useMemo(() => {
    const role = (activeMember.role || loggedUser.profile || '').toLowerCase();
    const id = (activeMember.id || loggedUser.nickname || '').toUpperCase();

    if (role.includes('admin') || role.includes('librarian') || role.includes('faculty')) return 50;
    if (id.includes('DTH') || role.includes('doctoral')) return 50;
    if (id.includes('LTH') || role.includes('licentiate')) return 30;
    return 20;
  }, [activeMember, loggedUser]);

  // KPI Calculations
  const kpis = useMemo(() => {
    const total = issuedItems.length;
    const booksCount = issuedItems.filter(i => i.itemType === 'Book').length;
    const journalsCount = issuedItems.filter(i => i.itemType === 'Journal' || i.itemType === 'Bound Volume').length;
    const overdueList = issuedItems.filter(i => i.status === 'OVERDUE');
    const overdueCount = overdueList.length;
    const totalFine = issuedItems.reduce((sum, i) => sum + (i.fine || 0), 0);
    const dueSoonCount = issuedItems.filter(i => i.status === 'DUE_SOON' || i.status === 'DUE_TODAY').length;
    const remainingQuota = Math.max(0, maxQuota - total);

    return {
      total,
      booksCount,
      journalsCount,
      overdueCount,
      totalFine,
      dueSoonCount,
      remainingQuota
    };
  }, [issuedItems, maxQuota]);

  // Sorting Handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Filtered & Sorted Display Items
  const displayItems = useMemo(() => {
    let list = [...issuedItems];

    // Filter by type / status
    if (activeFilter === 'BOOKS') {
      list = list.filter(i => i.itemType === 'Book');
    } else if (activeFilter === 'JOURNALS') {
      list = list.filter(i => i.itemType === 'Journal' || i.itemType === 'Bound Volume');
    } else if (activeFilter === 'OVERDUE') {
      list = list.filter(i => i.status === 'OVERDUE');
    } else if (activeFilter === 'DUE_SOON') {
      list = list.filter(i => i.status === 'DUE_SOON' || i.status === 'DUE_TODAY');
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(i =>
        (i.accessionNo || '').toLowerCase().includes(q) ||
        (i.bookTitle || i.title || '').toLowerCase().includes(q) ||
        (i.bookAuthor || i.author || '').toLowerCase().includes(q) ||
        (i.bookClassificationNo || i.classificationNo || '').toLowerCase().includes(q)
      );
    }

    // Sort
    list.sort((a, b) => {
      let valA = a[sortField] || '';
      let valB = b[sortField] || '';

      if (sortField === 'fine') {
        valA = Number(a.fine) || 0;
        valB = Number(b.fine) || 0;
      } else if (sortField === 'bookedTo' || sortField === 'bookedOn') {
        valA = a[sortField] ? new Date(a[sortField]).getTime() : 0;
        valB = b[sortField] ? new Date(b[sortField]).getTime() : 0;
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [issuedItems, activeFilter, searchTerm, sortField, sortOrder]);

  // Open details drawer
  const handleOpenDetail = (item) => {
    setSelectedItem(item);
    setShowDrawer(true);
  };

  // CSV Data Structure
  const csvHeaders = [
    { label: "Accession No", key: "accessionNo" },
    { label: "Item Type", key: "itemType" },
    { label: "Title", key: "bookTitle" },
    { label: "Author / Publisher", key: "bookAuthor" },
    { label: "Classification No", key: "bookClassificationNo" },
    { label: "Checkout Date", key: "bookedOn" },
    { label: "Due Date", key: "bookedTo" },
    { label: "Days Issued", key: "daysIssued" },
    { label: "Status", key: "status" },
    { label: "Fine (INR)", key: "fine" }
  ];

  return (
    <div className="mybooks-container">
      <Helmet>
        <title>My Borrowed Books & Journals | Vidyajyoti College of Theology Library</title>
        <meta name="description" content="View active books and theological journals issued against your account at Vidyajyoti Library." />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      {!loaded && <Preloader show={true} />}

      <div className="mybooks-wrapper">
        {/* Navigation Breadcrumbs & Header Actions */}
        <div className="mybooks-nav-header">
          <div className="mybooks-breadcrumb">
            <Link to="/">
              <i className="fa fa-home"></i> Home
            </Link>
            <span>/</span>
            <Link to="/myprofile">My Profile</Link>
            <span>/</span>
            <span style={{ color: '#0f172a', fontWeight: 600 }}>Active Circulation Loans</span>
          </div>

          <div className="mybooks-header-actions">
            <button
              type="button"
              className="btn-mybooks-action"
              onClick={() => window.print()}
              title="Print Current Circulation Statement"
            >
              <i className="fa fa-print"></i> Print Statement
            </button>

            {issuedItems.length > 0 && (
              <CSVLink
                data={issuedItems}
                headers={csvHeaders}
                filename={`vjlibrary-loans-${activeMember.id}-${new Date().toISOString().slice(0, 10)}.csv`}
                className="btn-mybooks-action"
                title="Download CSV Spreadsheet"
              >
                <i className="fa fa-file-excel text-success"></i> Export CSV
              </CSVLink>
            )}

            <Link to="/search" className="btn-mybooks-action btn-mybooks-action-primary">
              <i className="fa fa-search"></i> Browse Catalogue
            </Link>
          </div>
        </div>

        {/* Member Identity Hero Bar */}
        <div className="mybooks-member-hero">
          <div className="mybooks-member-left">
            <img
              src={activeMember.avatar || myProfileImage}
              alt="Member Avatar"
              className="mybooks-member-avatar"
              onError={(e) => {
                e.target.src = "https://s3.ap-south-1.amazonaws.com/idntica.com/avatar/profileImage/defaultAvatar.jpeg";
              }}
            />
            <div>
              <div className="mybooks-member-title">
                {activeMember.name || "Library Member"}
              </div>
              <div className="mybooks-member-subtitle">
                <span className="mybooks-member-badge">{activeMember.role || "Student"}</span>
                <span>•</span>
                <span className="mybooks-member-id-tag">
                  <i className="fa fa-id-card me-1"></i>
                  {activeMember.id || "NO-ID"}
                </span>
                <span>•</span>
                <span>Vidyajyoti College of Theology</span>
              </div>
            </div>
          </div>

          {/* Staff Member Lookup Tool (for Librarians/Admins) */}
          {isStaff && (
            <form onSubmit={handleStaffLookup} className="mybooks-staff-search">
              <i className="fa fa-user-check text-white-50"></i>
              <input
                type="text"
                className="mybooks-staff-input"
                placeholder="Lookup Reg / Staff ID..."
                value={lookupMemberId}
                onChange={(e) => setLookupMemberId(e.target.value)}
              />
              <button type="submit" className="btn-mybooks-staff-search">
                <i className="fa fa-search"></i> Lookup
              </button>
              {activeMember.id !== loggedUser.nickname && (
                <button
                  type="button"
                  onClick={handleResetToMyLoans}
                  className="btn btn-sm btn-outline-light ms-1"
                  style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                  title="Reset to My Personal Loans"
                >
                  My Loans
                </button>
              )}
            </form>
          )}
        </div>

        {/* Alert Banners */}
        {kpis.overdueCount > 0 && (
          <div className="mybooks-warning-banner">
            <i className="fa fa-exclamation-triangle fa-lg"></i>
            <div>
              You have <strong>{kpis.overdueCount} overdue {kpis.overdueCount === 1 ? 'item' : 'items'}</strong> with an accumulated fine of ₹{kpis.totalFine}. Please return or renew them promptly at the circulation desk.
            </div>
          </div>
        )}

        {kpis.overdueCount === 0 && kpis.total >= maxQuota - 2 && (
          <div className="mybooks-warning-banner info">
            <i className="fa fa-info-circle fa-lg"></i>
            <div>
              You have <strong>{kpis.total} items</strong> checked out, approaching your maximum quota of {maxQuota} books. Return completed readings to unlock additional checkouts.
            </div>
          </div>
        )}

        {/* KPI Metrics Summary Strip */}
        <div className="mybooks-kpi-grid">
          {/* Total Loans */}
          <div className="mybooks-kpi-card">
            <div className="mybooks-kpi-header">
              <span className="mybooks-kpi-label">
                <i className="fa fa-layer-group text-primary"></i> Total Checked Out
              </span>
              <div className="mybooks-kpi-icon" style={{ background: '#e0f2fe', color: '#0284c7' }}>
                <i className="fa fa-book-reader"></i>
              </div>
            </div>
            <div className="mybooks-kpi-value">{kpis.total} <span style={{ fontSize: '0.9rem', color: '#64748b' }}>/ {maxQuota} Max</span></div>
            <div className="mybooks-kpi-subtext">Active items in your custody</div>
          </div>

          {/* Books */}
          <div className="mybooks-kpi-card">
            <div className="mybooks-kpi-header">
              <span className="mybooks-kpi-label">
                <i className="fa fa-book text-info"></i> Books
              </span>
              <div className="mybooks-kpi-icon" style={{ background: '#e0f2fe', color: '#0369a1' }}>
                <i className="fa fa-book"></i>
              </div>
            </div>
            <div className="mybooks-kpi-value">{kpis.booksCount} <span style={{ fontSize: '0.9rem', color: '#64748b' }}>Books</span></div>
            <div className="mybooks-kpi-subtext">General & theological volumes</div>
          </div>

          {/* Journals */}
          <div className="mybooks-kpi-card">
            <div className="mybooks-kpi-header">
              <span className="mybooks-kpi-label">
                <i className="fa fa-newspaper text-purple"></i> Journals & Serials
              </span>
              <div className="mybooks-kpi-icon" style={{ background: '#f3e8ff', color: '#7e22ce' }}>
                <i className="fa fa-newspaper"></i>
              </div>
            </div>
            <div className="mybooks-kpi-value">{kpis.journalsCount} <span style={{ fontSize: '0.9rem', color: '#64748b' }}>Periodicals</span></div>
            <div className="mybooks-kpi-subtext">Issues & bound research volumes</div>
          </div>

          {/* Overdue / Fine */}
          <div className="mybooks-kpi-card">
            <div className="mybooks-kpi-header">
              <span className="mybooks-kpi-label">
                <i className="fa fa-coins text-warning"></i> Overdue & Fines
              </span>
              <div
                className="mybooks-kpi-icon"
                style={{
                  background: kpis.overdueCount > 0 ? '#fee2e2' : '#dcfce7',
                  color: kpis.overdueCount > 0 ? '#b91c1c' : '#15803d'
                }}
              >
                <i className={`fa ${kpis.overdueCount > 0 ? 'fa-exclamation' : 'fa-check'}`}></i>
              </div>
            </div>
            <div className="mybooks-kpi-value" style={{ color: kpis.overdueCount > 0 ? '#b91c1c' : '#059669' }}>
              {kpis.overdueCount > 0 ? `₹${kpis.totalFine}` : '₹0'}
              <span style={{ fontSize: '0.85rem', color: '#64748b', marginLeft: '6px' }}>
                ({kpis.overdueCount} Overdue)
              </span>
            </div>
            <div className="mybooks-kpi-subtext">
              {kpis.overdueCount > 0 ? 'Please clear with circulation desk' : 'Account in good standing'}
            </div>
          </div>
        </div>

        {/* Filter Toolbar & Search */}
        <div className="mybooks-toolbar">
          <div className="mybooks-filter-chips">
            <button
              type="button"
              className={`mybooks-chip ${activeFilter === 'ALL' ? 'active' : ''}`}
              onClick={() => setActiveFilter('ALL')}
            >
              <span>All Items</span>
              <span className="mybooks-chip-count">{issuedItems.length}</span>
            </button>

            <button
              type="button"
              className={`mybooks-chip ${activeFilter === 'BOOKS' ? 'active' : ''}`}
              onClick={() => setActiveFilter('BOOKS')}
            >
              <i className="fa fa-book"></i>
              <span>Books</span>
              <span className="mybooks-chip-count">{kpis.booksCount}</span>
            </button>

            <button
              type="button"
              className={`mybooks-chip ${activeFilter === 'JOURNALS' ? 'active' : ''}`}
              onClick={() => setActiveFilter('JOURNALS')}
            >
              <i className="fa fa-newspaper"></i>
              <span>Journals & Serials</span>
              <span className="mybooks-chip-count">{kpis.journalsCount}</span>
            </button>

            {kpis.dueSoonCount > 0 && (
              <button
                type="button"
                className={`mybooks-chip ${activeFilter === 'DUE_SOON' ? 'active' : ''}`}
                onClick={() => setActiveFilter('DUE_SOON')}
              >
                <i className="fa fa-clock text-warning"></i>
                <span>Due Soon</span>
                <span className="mybooks-chip-count">{kpis.dueSoonCount}</span>
              </button>
            )}

            {kpis.overdueCount > 0 && (
              <button
                type="button"
                className={`mybooks-chip ${activeFilter === 'OVERDUE' ? 'active' : ''}`}
                onClick={() => setActiveFilter('OVERDUE')}
              >
                <i className="fa fa-exclamation-circle text-danger"></i>
                <span>Overdue</span>
                <span className="mybooks-chip-count">{kpis.overdueCount}</span>
              </button>
            )}
          </div>

          <div className="mybooks-search-box">
            <i className="fa fa-search mybooks-search-icon"></i>
            <input
              type="text"
              className="mybooks-search-input"
              placeholder="Search title, author, accession..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Issued Books & Journals Table Card */}
        {displayItems.length === 0 ? (
          <div className="mybooks-empty-state">
            <div className="mybooks-empty-icon">
              <i className="fa fa-book-open"></i>
            </div>
            <div className="mybooks-empty-title">
              {issuedItems.length === 0 ? "No Books or Journals Checked Out" : "No Matching Items Found"}
            </div>
            <div className="mybooks-empty-text">
              {issuedItems.length === 0
                ? "You currently have no active library loans. Explore our rich digital repository of theology books, journals, and commentaries to enhance your research."
                : "No issued items match your current filter or search criteria. Try selecting 'All Items' or clearing the search box."}
            </div>
            <Link to="/search" className="btn-mybooks-action btn-mybooks-action-primary">
              <i className="fa fa-search"></i> Explore Library Catalogue
            </Link>
          </div>
        ) : (
          <div className="mybooks-table-card">
            <div style={{ overflowX: 'auto' }}>
              <table className="mybooks-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>#</th>
                    <th className="sortable" onClick={() => handleSort('accessionNo')}>
                      Accession / Call No
                      <i className={`fa mybooks-sort-icon ${sortField === 'accessionNo' ? (sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}></i>
                    </th>
                    <th className="sortable" onClick={() => handleSort('bookTitle')}>
                      Title & Bibliographic Info
                      <i className={`fa mybooks-sort-icon ${sortField === 'bookTitle' ? (sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}></i>
                    </th>
                    <th className="sortable" onClick={() => handleSort('bookedOn')}>
                      Issued Date
                      <i className={`fa mybooks-sort-icon ${sortField === 'bookedOn' ? (sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}></i>
                    </th>
                    <th className="sortable" onClick={() => handleSort('bookedTo')}>
                      Return Due Date
                      <i className={`fa mybooks-sort-icon ${sortField === 'bookedTo' ? (sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}></i>
                    </th>
                    <th className="sortable" onClick={() => handleSort('fine')}>
                      Loan Status
                      <i className={`fa mybooks-sort-icon ${sortField === 'fine' ? (sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}></i>
                    </th>
                    <th style={{ textAlign: 'center', width: '90px' }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((item, index) => {
                    const isOverdue = item.status === 'OVERDUE';
                    const isDueToday = item.status === 'DUE_TODAY';
                    const isDueSoon = item.status === 'DUE_SOON';

                    return (
                      <tr key={item.accessionNo || index} className={isOverdue ? 'overdue-row' : ''}>
                        <td style={{ color: '#94a3b8', fontWeight: 600 }}>{index + 1}</td>

                        {/* Accession & Classification */}
                        <td>
                          <span className="mybooks-accession-pill">{item.accessionNo}</span>
                          {item.bookClassificationNo && (
                            <div className="mybooks-callno-text">
                              <i className="fa fa-tag me-1"></i>
                              {item.bookClassificationNo}
                            </div>
                          )}
                        </td>

                        {/* Title & Item Type */}
                        <td>
                          <div className="mybooks-item-cell">
                            <div>
                              <div style={{ marginBottom: '4px' }} className="d-flex align-items-center gap-1 flex-wrap">
                                <span className={`mybooks-item-type-badge ${item.itemType === 'Journal' ? 'badge-journal' : item.itemType === 'Bound Volume' ? 'badge-bound' : 'badge-book'}`}>
                                  <i className={`fa ${item.itemType === 'Journal' ? 'fa-newspaper' : item.itemType === 'Bound Volume' ? 'fa-archive' : 'fa-book'}`}></i>
                                  {item.itemType}
                                </span>
                                {((item.bookScope || '').toLowerCase() === 'reference' || (item.scope || '').toLowerCase() === 'reference') && (
                                  <span className="badge bg-warning text-dark px-2 py-1" style={{ fontSize: '0.72rem', borderRadius: '6px' }}>
                                    <i className="fa fa-bookmark me-1"></i> Reference (1-Day Loan)
                                  </span>
                                )}
                              </div>
                              <div className="mybooks-title-text">{item.bookTitle || item.title || 'Untitled Publication'}</div>
                              <div className="mybooks-author-text">
                                <i className="fa fa-user-edit me-1"></i>
                                {item.bookAuthor || item.author || 'Author / Publisher not listed'}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Issued Date */}
                        <td>
                          <div style={{ fontWeight: 600, color: '#334155' }}>
                            {item.bookedOn || 'N/A'}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                            {item.daysIssued} {item.daysIssued === 1 ? 'day' : 'days'} held
                          </div>
                        </td>

                        {/* Due Date */}
                        <td>
                          <div style={{ fontWeight: 700, color: isOverdue ? '#b91c1c' : '#0f172a' }}>
                            {item.bookedTo || 'N/A'}
                          </div>
                          <div style={{ fontSize: '0.78rem' }}>
                            {isOverdue && (
                              <span style={{ color: '#dc2626', fontWeight: 600 }}>
                                <i className="fa fa-exclamation-triangle me-1"></i>
                                {item.overdueDays} {item.overdueDays === 1 ? 'day' : 'days'} overdue
                              </span>
                            )}
                            {isDueToday && (
                              <span style={{ color: '#ea580c', fontWeight: 600 }}>
                                <i className="fa fa-clock me-1"></i> Due by 5:00 PM today
                              </span>
                            )}
                            {isDueSoon && (
                              <span style={{ color: '#d97706', fontWeight: 600 }}>
                                <i className="fa fa-hourglass-half me-1"></i> {item.daysLeft} days left
                              </span>
                            )}
                            {!isOverdue && !isDueToday && !isDueSoon && (
                              <span style={{ color: '#16a34a', fontWeight: 500 }}>
                                {item.daysLeft} days left
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Status Badge */}
                        <td>
                          {isOverdue && (
                            <div className="loan-status-pill loan-status-overdue">
                              <i className="fa fa-exclamation-circle"></i>
                              <span>Overdue • Fine ₹{item.fine}</span>
                            </div>
                          )}
                          {isDueToday && (
                            <div className="loan-status-pill loan-status-due-today">
                              <i className="fa fa-bell"></i>
                              <span>Due Today</span>
                            </div>
                          )}
                          {isDueSoon && (
                            <div className="loan-status-pill loan-status-due-soon">
                              <i className="fa fa-clock"></i>
                              <span>Due in {item.daysLeft} Days</span>
                            </div>
                          )}
                          {!isOverdue && !isDueToday && !isDueSoon && (
                            <div className="loan-status-pill loan-status-active">
                              <i className="fa fa-check"></i>
                              <span>Active Loan</span>
                            </div>
                          )}
                        </td>

                        {/* Detail Action */}
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn-item-detail"
                            onClick={() => handleOpenDetail(item)}
                            title="View Full Item Details"
                          >
                            <i className="fa fa-eye"></i> View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Item Details Offcanvas Drawer */}
      <Offcanvas
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        placement="end"
        style={{ width: '450px', maxWidth: '95vw' }}
      >
        <Offcanvas.Header closeButton className="detail-drawer-header">
          <Offcanvas.Title>
            <i className={`fa ${selectedItem?.itemType === 'Journal' ? 'fa-newspaper' : 'fa-book'} me-2`}></i>
            {selectedItem?.itemType} Circulation Details
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="detail-drawer-body">
          {selectedItem && (
            <div>
              {/* Cover Preview Hero */}
              <div className="detail-preview-hero">
                <BookCover
                  title={selectedItem.bookTitle || selectedItem.title}
                  author={selectedItem.bookAuthor || selectedItem.author}
                  accessionNo={selectedItem.accessionNo}
                  callNumber={selectedItem.bookClassificationNo}
                  category={selectedItem.bookCategory}
                  size="sm"
                />
                <div>
                  <span className={`mybooks-item-type-badge ${selectedItem.itemType === 'Journal' ? 'badge-journal' : selectedItem.itemType === 'Bound Volume' ? 'badge-bound' : 'badge-book'} mb-2`}>
                    {selectedItem.itemType}
                  </span>
                  <h6 style={{ fontWeight: 800, color: '#063547', marginBottom: '4px' }}>
                    {selectedItem.bookTitle || selectedItem.title}
                  </h6>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0' }}>
                    {selectedItem.bookAuthor || selectedItem.author}
                  </p>
                </div>
              </div>

              {/* Barcode Strip */}
              {selectedItem.accessionNo && (
                <div style={{ background: '#ffffff', padding: '12px', borderRadius: '10px', textAlign: 'center', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
                  <Barcode
                    value={selectedItem.accessionNo}
                    width={1.2}
                    height={38}
                    fontSize={11}
                    margin={0}
                  />
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px', fontWeight: 600 }}>
                    Circulation Accession Barcode
                  </div>
                </div>
              )}

              {/* Metadata List */}
              <div className="detail-meta-list">
                <div className="detail-meta-item">
                  <span className="detail-meta-label">Accession Number</span>
                  <span className="detail-meta-val" style={{ fontFamily: 'monospace', color: '#0284c7' }}>
                    {selectedItem.accessionNo}
                  </span>
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Classification / Call No</span>
                  <span className="detail-meta-val">{selectedItem.bookClassificationNo || 'N/A'}</span>
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Book Scope</span>
                  <span className="detail-meta-val">
                    {((selectedItem.bookScope || '').toLowerCase() === 'reference' || (selectedItem.scope || '').toLowerCase() === 'reference') ? (
                      <span className="badge bg-warning text-dark">Reference (1-Day Student Loan)</span>
                    ) : (
                      <span className="badge bg-light text-dark border">Circulation (Standard Loan)</span>
                    )}
                  </span>
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Checkout Date</span>
                  <span className="detail-meta-val">{selectedItem.bookedOn || 'N/A'}</span>
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Return Due Date</span>
                  <span className="detail-meta-val" style={{ color: selectedItem.status === 'OVERDUE' ? '#b91c1c' : '#0f172a' }}>
                    {selectedItem.bookedTo || 'N/A'}
                  </span>
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Total Days Issued</span>
                  <span className="detail-meta-val">{selectedItem.daysIssued} Days</span>
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Loan Status</span>
                  <span className="detail-meta-val">
                    {selectedItem.status === 'OVERDUE' ? (
                      <span className="text-danger fw-bold">Overdue by {selectedItem.overdueDays} days (Fine: ₹{selectedItem.fine})</span>
                    ) : selectedItem.status === 'DUE_TODAY' ? (
                      <span className="text-warning fw-bold">Due Today</span>
                    ) : (
                      <span className="text-success fw-bold">Active ({selectedItem.daysLeft} days remaining)</span>
                    )}
                  </span>
                </div>

                {selectedItem.bookCategory && (
                  <div className="detail-meta-item">
                    <span className="detail-meta-label">Subject Category</span>
                    <span className="detail-meta-val">{selectedItem.bookCategory}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{ marginTop: '24px', display: 'flex', gap: '10px' }}>
                <Link
                  to="/search"
                  className="btn btn-outline-secondary w-100"
                  style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.88rem' }}
                >
                  <i className="fa fa-search me-1"></i> Find Similar
                </Link>
                {isStaff && (
                  <Link
                    to="/issuereturnbooks"
                    className="btn btn-primary w-100"
                    style={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.88rem', background: '#063547', borderColor: '#063547' }}
                  >
                    <i className="fa fa-exchange-alt me-1"></i> Return Desk
                  </Link>
                )}
              </div>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}
